// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
//
// CASAVIO TONE PRINCIPLE: Never judge the user's home, possessions, cleanliness,
// or organization. UI copy stays neutral and factual. Do NOT add condition / wear /
// clutter / housekeeping language to any label, button, banner, or empty state.
import { useState, useRef, useEffect } from 'react'
import { Camera, X, Loader, ChevronRight, Trash2, Plus, Image as ImageIcon } from 'lucide-react'
import { supabase } from '../supabase'
import { convertHeicIfNeeded } from '../utils/convertHeicIfNeeded'
import { stripExif } from '../utils/stripExif'
import { compressImage } from '../imageUtils'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

// Must match RoomDetail.js INVENTORY_CATEGORIES exactly. Both lists are the
// contract with the Edge Function's category_suggestion field.
const CATEGORIES = [
  'Furniture', 'Electronics', 'Appliances',
  'Housewares', 'Linens', 'Décor',
  'Bags & Luggage', 'Books & Media',
  'Jewelry', 'Art', 'Clothing', 'Tools',
  'Musical Instruments', 'Sports Equipment', 'Collectibles',
  'Other',
]
const CATEGORIES_SET = new Set(CATEGORIES)

const CATEGORY_MAP = [
  { kw: ['television', 'tv', 'flat screen', 'flat-screen', 'monitor', 'computer', 'laptop', 'printer', 'speaker', 'router', 'console', 'game'], cat: 'Electronics' },
  { kw: ['chair', 'sofa', 'couch', 'desk', 'table', 'bed', 'dresser', 'nightstand', 'bookshelf', 'shelf', 'cabinet', 'ottoman', 'stool'], cat: 'Furniture' },
  { kw: ['refrigerator', 'microwave', 'oven', 'dishwasher', 'washer', 'dryer'], cat: 'Appliances' },
  { kw: ['dish', 'plate', 'bowl', 'glass', 'mug', 'cup', 'pot', 'pan', 'mixer'], cat: 'Housewares' },
  { kw: ['towel', 'sheet', 'blanket', 'curtain', 'bedding', 'pillowcase', 'linen'], cat: 'Linens' },
  { kw: ['lamp', 'light', 'vase', 'candle', 'throw pillow', 'ornament', 'wall hanging'], cat: 'Décor' },
  { kw: ['bag', 'backpack', 'suitcase', 'tote', 'purse', 'luggage'], cat: 'Bags & Luggage' },
  { kw: ['book', 'dvd', 'vinyl', 'record'], cat: 'Books & Media' },
  { kw: ['painting', 'art', 'sculpture', 'frame'], cat: 'Art' },
  { kw: ['guitar', 'piano', 'keyboard', 'drum', 'violin'], cat: 'Musical Instruments' },
]

function keywordGuessCategory(label) {
  if (!label) return 'Other'
  const lower = label.toLowerCase()
  for (const m of CATEGORY_MAP) {
    if (m.kw.some(k => lower.includes(k))) return m.cat
  }
  return 'Other'
}

// Prefer Claude's category_suggestion if it's a valid category; otherwise fall
// back to the keyword map; otherwise 'Other'.
function resolveCategory(label, suggestion) {
  if (typeof suggestion === 'string' && CATEGORIES_SET.has(suggestion)) {
    return suggestion
  }
  return keywordGuessCategory(label)
}

function confDotColor(conf) {
  if (conf === 'high') return C.success
  if (conf === 'medium') return C.amber
  return C.muted
}

function confTooltip(conf) {
  if (conf === 'high') return 'AI is highly confident'
  if (conf === 'medium') return "AI's best guess — please verify"
  return 'AI is uncertain — please review'
}

const inp = {
  width: '100%', background: '#fff', border: `1px solid ${C.border}`,
  borderRadius: '8px', padding: '10px 12px', fontSize: '15px',
  fontFamily: fs, color: C.brown, boxSizing: 'border-box', outline: 'none',
  WebkitAppearance: 'none',
}
const lbl = {
  fontSize: '10px', color: C.muted, letterSpacing: '1.5px',
  textTransform: 'uppercase', fontFamily: fs, fontWeight: '600',
  display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px',
}

// Fill-nulls merge against a v9 single_item ai_details payload. Same policy
// as the existing per-card "Get details" flow (handleDetailsFileSelected),
// adapted to the v9 ClaudeDetails shape ({ brand: {value, confidence}, ... })
// instead of the identify-appliance flat shape ({ make, make_confidence }).
//
// Rules:
//   brand          fills if empty AND ai brand confidence is high|medium
//   model_number   fills if empty AND ai model confidence is high|medium
//   serial_number  fills if empty AND ai serial is a non-empty string
//   manufactured   fills if empty AND ai manufactured is a non-empty string
//   color, material, refined_label, notes, category — NEVER overwritten
//
// Returns { next, filledFields } so callers can build a user-facing summary.
export function mergeFillNullsFromSingleItemAi(target, aiDetails) {
  if (!aiDetails || typeof aiDetails !== 'object') {
    return { next: target, filledFields: [] }
  }
  const next = { ...target }
  const filled = []
  const isEmpty = v => v == null || (typeof v === 'string' && v.trim() === '')
  const allowedConf = c => c === 'high' || c === 'medium'

  if (
    isEmpty(next.brand)
    && aiDetails.brand && typeof aiDetails.brand.value === 'string'
    && aiDetails.brand.value.trim().length > 0
    && allowedConf(aiDetails.brand.confidence)
  ) {
    next.brand = aiDetails.brand.value
    next.brand_confidence = aiDetails.brand.confidence
    filled.push('brand')
  }
  if (
    isEmpty(next.model_number)
    && aiDetails.model && typeof aiDetails.model.value === 'string'
    && aiDetails.model.value.trim().length > 0
    && allowedConf(aiDetails.model.confidence)
  ) {
    next.model_number = aiDetails.model.value
    next.model_confidence = aiDetails.model.confidence
    filled.push('model number')
  }
  if (isEmpty(next.serial_number) && typeof aiDetails.serial === 'string' && aiDetails.serial.trim().length > 0) {
    next.serial_number = aiDetails.serial
    filled.push('serial number')
  }
  if (isEmpty(next.manufactured) && typeof aiDetails.manufactured === 'string' && aiDetails.manufactured.trim().length > 0) {
    next.manufactured = aiDetails.manufactured
    filled.push('manufactured date')
  }
  return { next, filledFields: filled }
}

export function summaryFromFilled(fields) {
  if (!fields || fields.length === 0) return 'Photo added'
  if (fields.length === 1) return `Added ${fields[0]}`
  return `Added ${fields.length} details`
}

function ConfDot({ conf }) {
  if (!conf) return null
  return (
    <span
      title={confTooltip(conf)}
      style={{
        display: 'inline-block', width: '8px', height: '8px',
        borderRadius: '50%', background: confDotColor(conf),
        flexShrink: 0,
      }}
    />
  )
}

const SPIN_STYLE = `@keyframes roomscan-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`

export default function RoomScan({ room, homeId, onClose, onSaved, sourcePreference = 'camera', initialReview = null }) {
  // initialReview lets a parent (e.g. ScanRoom) seed the review screen with a
  // pre-fetched Edge Function response, skipping the capture/processing UI.
  const [state, setState] = useState(initialReview ? 'review' : 'capture') // capture | processing | review | saving | error
  const [errorMessage, setErrorMessage] = useState(null)
  const [errorContext, setErrorContext] = useState(null) // 'ai' | 'save'
  const [response, setResponse] = useState(null)
  const [items, setItems] = useState([])
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [showManualAdd, setShowManualAdd] = useState(false)
  const [showAll, setShowAll] = useState(false) // toggle the "See all" / "Hide non-essential" view

  // Edit view state — tapping a card opens a full-screen edit overlay above the review.
  const [editingItemId, setEditingItemId] = useState(null)
  const [editDraft, setEditDraft] = useState(null)
  const [editOriginal, setEditOriginal] = useState(null)
  const [editConfirmDiscard, setEditConfirmDiscard] = useState(false)
  const [editConfirmDelete, setEditConfirmDelete] = useState(false)
  const [editPhotoUploading, setEditPhotoUploading] = useState(false)
  const [editPhotoError, setEditPhotoError] = useState(null)
  // Per-Detail-Photos AI extraction: when enabled, every new photo runs
  // capture_mode='single_item' and fill-nulls-merges into editDraft.
  const [editExtractEnabled, setEditExtractEnabled] = useState(true)
  // Keyed by signed photo URL (stable across re-renders + index shifts when
  // the user removes photos). { [url]: { state: 'extracting'|'success'|'error', summary?: string } }
  const [editDetailExtractions, setEditDetailExtractions] = useState({})
  const [manualForm, setManualForm] = useState({
    name: '', category: 'Other', brand: '', model_number: '', color: '', material: '', size: '',
  })
  const [savedBase64, setSavedBase64] = useState(null) // kept for retry

  // Per-item "Get details" state, keyed by item.localId.
  const [detailsInFlight, setDetailsInFlight] = useState({})
  const [detailsErrors, setDetailsErrors] = useState({})
  const [detailsSuccess, setDetailsSuccess] = useState({})

  const cameraInputRef = useRef(null)   // capture="environment" — forces rear camera
  const libraryInputRef = useRef(null)  // no capture — iOS native sheet (camera/library)
  const editCameraRef = useRef(null)    // edit-view detail photo, camera-forced
  const editLibraryRef = useRef(null)   // edit-view detail photo, library
  const detailsInputRef = useRef(null)  // per-item Get-details capture input
  const detailsItemIdRef = useRef(null) // localId of the item the current capture is for
  const cardRefs = useRef({})
  const photoSectionRef = useRef(null)
  const localIdCounterRef = useRef(0)
  const autoLaunchedRef = useRef(false) // gates one-time auto-launch on initial mount

  const nextLocalId = (prefix) => {
    localIdCounterRef.current += 1
    return `${prefix}-${localIdCounterRef.current}`
  }

  // Single source of truth for turning a v6 Edge Function response into the
  // local items[] shape. Used both by runEdgeFunction (live capture) and by
  // the initialReview seed effect (handoff from ScanRoom).
  const buildItemsFromResponse = (data) => {
    return (data?.items || []).map((it, i) => {
      const shouldLog = it.ai_details && typeof it.ai_details.should_log === 'boolean'
        ? it.ai_details.should_log
        : true
      const resolvedCategory = resolveCategory(
        it.refined_label || it.label,
        it.ai_details?.category_suggestion,
      )
      return {
        localId: nextLocalId('ai'),
        overlayNumber: i + 1,
        label: it.label,
        refined_label: it.refined_label || it.label,
        confidence_band: it.confidence_band,
        confidence_score: it.confidence_score,
        bounding_box: it.bounding_box,
        // Multi-frame fields (v6 server). Default to single-frame semantics
        // when the server omits them (e.g. legacy photo response).
        frame_index: typeof it.frame_index === 'number' ? it.frame_index : 0,
        frame_indices: Array.isArray(it.frame_indices) && it.frame_indices.length > 0
          ? it.frame_indices
          : [typeof it.frame_index === 'number' ? it.frame_index : 0],
        times_seen: typeof it.times_seen === 'number' && it.times_seen > 0 ? it.times_seen : 1,
        thumbnail_url: it.thumbnail_url,
        claude_extracted: it.claude_extracted,
        ai_extracted: true,
        should_log: shouldLog,
        selected: shouldLog,
        name: it.refined_label || it.label,
        category: resolvedCategory,
        brand: it.ai_details?.brand?.value || '',
        brand_confidence: it.ai_details?.brand?.confidence || null,
        model_number: it.ai_details?.model?.value || '',
        model_confidence: it.ai_details?.model?.confidence || null,
        serial_number: '',
        manufactured: '',
        color: it.ai_details?.color || '',
        material: it.ai_details?.material || '',
        size: it.ai_details?.size_estimate?.value || '',
        size_confidence: it.ai_details?.size_estimate?.confidence || null,
        notes: '',
        photo_urls: [],
      }
    })
  }

  // Seed-from-initialReview: when ScanRoom hands off a pre-fetched response,
  // populate the review screen on mount. The auto-launch effect below is
  // suppressed because state is already 'review'.
  useEffect(() => {
    if (!initialReview) return
    setResponse(initialReview)
    setItems(buildItemsFromResponse(initialReview))
    autoLaunchedRef.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-launch the file picker once on initial mount based on sourcePreference.
  // If the user dismisses the picker, they land on the capture intro screen as a fallback.
  useEffect(() => {
    if (state !== 'capture') return
    if (autoLaunchedRef.current) return
    autoLaunchedRef.current = true
    const handle = requestAnimationFrame(() => {
      if (sourcePreference === 'library') {
        libraryInputRef.current?.click()
      } else {
        cameraInputRef.current?.click()
      }
    })
    return () => cancelAnimationFrame(handle)
  }, [state, sourcePreference])

  const handleHeaderClose = () => {
    if (state === 'review' && items.length > 0) {
      setConfirmDiscard(true)
    } else if (state === 'processing' || state === 'saving') {
      // No close mid-flight.
      return
    } else {
      onClose()
    }
  }

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    // Show the processing spinner BEFORE we start any potentially slow work
    // (HEIC conversion on a 4MB iPhone photo takes 1-3 seconds).
    setState('processing')
    setErrorMessage(null)
    setErrorContext(null)

    let processedFile
    try {
      // Step 1: convert HEIC to JPEG if needed (iPhone default format).
      const jpegFile = await convertHeicIfNeeded(file)
      // Step 2: compress to a reasonable upload size.
      processedFile = await compressImage(jpegFile, 1600, 0.80)
    } catch (err) {
      console.error('[RoomScan] preprocess error', err)
      setErrorMessage(err?.message || 'Could not process this photo. Please try another.')
      setErrorContext('ai')
      setState('error')
      return
    }

    const reader = new FileReader()
    reader.onload = (ev) => {
      const dataUrl = ev.target.result
      const base64 = String(dataUrl).split(',')[1] || ''
      setSavedBase64(base64)
      runEdgeFunction(base64)
    }
    reader.onerror = () => {
      setErrorMessage('Something went wrong scanning the room. Please try again.')
      setErrorContext('ai')
      setState('error')
    }
    reader.readAsDataURL(processedFile)
  }

  const runEdgeFunction = async (base64) => {
    setState('processing')
    setErrorMessage(null)
    setErrorContext(null)
    try {
      const { data, error } = await supabase.functions.invoke('enumerate-room-items', {
        body: {
          photo_base64: base64,
          room_id: room.id,
          home_id: homeId,
          media_type: 'image/jpeg',
        },
      })
      if (error) {
        console.error('[RoomScan] edge function error', error)
        setErrorMessage('Something went wrong scanning the room. Please try again.')
        setErrorContext('ai')
        setState('error')
        return
      }
      if (!data || !data.items_detected || data.items_detected === 0) {
        setErrorMessage("We didn't find any items in this photo. Try a wider angle with better lighting.")
        setErrorContext('ai')
        setState('error')
        return
      }
      setResponse(data)
      const built = buildItemsFromResponse(data)
      setItems(built)
      setState('review')
    } catch (err) {
      console.error('[RoomScan] edge function exception', err)
      setErrorMessage('Something went wrong scanning the room. Please try again.')
      setErrorContext('ai')
      setState('error')
    }
  }

  const updateItem = (id, patch) => {
    setItems(prev => prev.map(it => it.localId === id ? { ...it, ...patch } : it))
  }

  const deleteItem = (id) => {
    setItems(prev => prev.filter(it => it.localId !== id))
  }

  const openEditView = (item) => {
    setEditDraft({ ...item, photo_urls: Array.isArray(item.photo_urls) ? [...item.photo_urls] : [] })
    setEditOriginal({ ...item, photo_urls: Array.isArray(item.photo_urls) ? [...item.photo_urls] : [] })
    setEditingItemId(item.localId)
    setEditConfirmDiscard(false)
    setEditConfirmDelete(false)
    setEditPhotoError(null)
    setEditExtractEnabled(true)
    setEditDetailExtractions({})
  }

  const isEditDirty = () => {
    if (!editDraft || !editOriginal) return false
    const textKeys = ['name', 'brand', 'model_number', 'serial_number', 'category', 'color', 'material', 'notes']
    for (const k of textKeys) {
      if ((editDraft[k] || '') !== (editOriginal[k] || '')) return true
    }
    const a = editDraft.photo_urls || []
    const b = editOriginal.photo_urls || []
    if (a.length !== b.length) return true
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return true
    return false
  }

  const closeEditView = (commit) => {
    if (commit && editDraft && editingItemId) {
      const id = editingItemId
      const draft = editDraft
      setItems(prev => prev.map(it => it.localId === id ? { ...it, ...draft } : it))
    }
    setEditingItemId(null)
    setEditDraft(null)
    setEditOriginal(null)
    setEditConfirmDiscard(false)
    setEditConfirmDelete(false)
    setEditPhotoError(null)
  }

  const handleEditClose = () => {
    if (isEditDirty()) {
      setEditConfirmDiscard(true)
    } else {
      closeEditView(false)
    }
  }

  const handleEditDelete = () => setEditConfirmDelete(true)

  const confirmEditDelete = () => {
    if (editingItemId) deleteItem(editingItemId)
    closeEditView(false)
  }

  const setEditField = (k, v) => setEditDraft(prev => prev ? { ...prev, [k]: v } : prev)

  const handleEditPhotoSelect = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !editDraft) return
    const currentCount = editDraft.photo_urls?.length || 0
    if (currentCount >= 10) {
      setEditPhotoError("You've reached the limit of 10 detail photos.")
      return
    }
    setEditPhotoUploading(true)
    setEditPhotoError(null)
    let processedForExtraction = null
    let signedUrlForExtraction = null
    try {
      const jpeg = await convertHeicIfNeeded(file)
      const stripped = await stripExif(jpeg)
      const processed = await compressImage(stripped, 1600, 0.80)
      processedForExtraction = processed
      const userResp = await supabase.auth.getUser()
      const userId = userResp?.data?.user?.id
      const sessionId = response?.session_id
      if (!userId || !sessionId) {
        throw new Error('Session expired. Please retry the scan.')
      }
      const path = `${userId}/${sessionId}/details/${editingItemId}/${Date.now()}.jpg`
      const { error: upErr } = await supabase.storage
        .from('room-scans')
        .upload(path, processed, { contentType: 'image/jpeg', upsert: false })
      if (upErr) throw upErr
      const { data: signed, error: signErr } = await supabase.storage
        .from('room-scans')
        .createSignedUrl(path, 60 * 60 * 24 * 365)
      if (signErr || !signed?.signedUrl) throw new Error('Could not generate a link for the photo.')
      signedUrlForExtraction = signed.signedUrl
      setEditDraft(prev => prev
        ? { ...prev, photo_urls: [...(prev.photo_urls || []), signed.signedUrl] }
        : prev,
      )
    } catch (err) {
      console.error('[RoomScan editView] photo upload error', err)
      setEditPhotoError(err.message || 'Could not add that photo. Please try another.')
      setEditPhotoUploading(false)
      return
    }
    setEditPhotoUploading(false)

    // Fire-and-forget AI extraction on the new photo if the toggle is on.
    if (editExtractEnabled && processedForExtraction && signedUrlForExtraction) {
      runEditPhotoExtraction(signedUrlForExtraction, processedForExtraction)
    }
  }

  const runEditPhotoExtraction = async (photoUrl, processedBlob) => {
    setEditDetailExtractions(prev => ({ ...prev, [photoUrl]: { state: 'extracting' } }))

    let base64
    try {
      base64 = await new Promise((resolve, reject) => {
        const r = new FileReader()
        r.onload = () => resolve(String(r.result).split(',')[1] || '')
        r.onerror = () => reject(new Error('FileReader failed'))
        r.readAsDataURL(processedBlob)
      })
    } catch (err) {
      console.error('[RoomScan editView] extraction base64 error', err)
      setEditDetailExtractions(prev => ({ ...prev, [photoUrl]: { state: 'error' } }))
      return
    }

    try {
      const { data, error } = await supabase.functions.invoke('enumerate-room-items', {
        body: {
          capture_mode: 'single_item',
          photo_base64: base64,
          room_id: room.id,
          home_id: homeId,
          media_type: 'image/jpeg',
        },
      })
      if (error) throw new Error(error.message || 'function failed')
      const ai = data?.items?.[0]?.ai_details

      let filledFields = []
      setEditDraft(prev => {
        if (!prev) return prev
        const { next, filledFields: filled } = mergeFillNullsFromSingleItemAi(prev, ai)
        filledFields = filled
        return next
      })

      const summary = summaryFromFilled(filledFields)
      setEditDetailExtractions(prev => ({ ...prev, [photoUrl]: { state: 'success', summary } }))
      setTimeout(() => {
        setEditDetailExtractions(prev => {
          const next = { ...prev }
          delete next[photoUrl]
          return next
        })
      }, 2000)
    } catch (err) {
      console.error('[RoomScan editView] extraction error', err)
      setEditDetailExtractions(prev => ({ ...prev, [photoUrl]: { state: 'error' } }))
    }
  }

  const removeEditPhoto = (idx) => {
    setEditDraft(prev => {
      if (!prev) return prev
      const photos = prev.photo_urls || []
      const removedUrl = photos[idx]
      if (removedUrl) {
        setEditDetailExtractions(map => {
          if (!(removedUrl in map)) return map
          const next = { ...map }
          delete next[removedUrl]
          return next
        })
      }
      return { ...prev, photo_urls: photos.filter((_, i) => i !== idx) }
    })
  }

  // ----- Get details (per-item close-up → identify-appliance merge) ---------

  const setItemError = (itemId, msg) => {
    setDetailsErrors(prev => ({ ...prev, [itemId]: msg }))
  }
  const clearItemError = (itemId) => {
    setDetailsErrors(prev => {
      const next = { ...prev }
      delete next[itemId]
      return next
    })
  }
  const flashItemSuccess = (itemId) => {
    setDetailsSuccess(prev => ({ ...prev, [itemId]: true }))
    setTimeout(() => {
      setDetailsSuccess(prev => {
        const next = { ...prev }
        delete next[itemId]
        return next
      })
    }, 2000)
  }
  const setItemInFlight = (itemId, on) => {
    setDetailsInFlight(prev => {
      if (on) return { ...prev, [itemId]: true }
      const next = { ...prev }
      delete next[itemId]
      return next
    })
  }

  const handleGetDetailsClick = (item) => {
    if (detailsInFlight[item.localId]) return
    clearItemError(item.localId)
    detailsItemIdRef.current = item.localId
    detailsInputRef.current?.click()
  }

  const handleDetailsFileSelected = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    const itemId = detailsItemIdRef.current
    detailsItemIdRef.current = null
    if (!file || !itemId) return

    const item = items.find(it => it.localId === itemId)
    if (!item) return

    setItemInFlight(itemId, true)
    clearItemError(itemId)

    let processed
    try {
      const jpeg = await convertHeicIfNeeded(file)
      const stripped = await stripExif(jpeg)
      processed = await compressImage(stripped, 1600, 0.80)
    } catch (err) {
      console.error('[RoomScan getDetails] preprocess failed', err)
      setItemError(itemId, "Couldn't read details — try again")
      setItemInFlight(itemId, false)
      return
    }

    const mediaType = (processed && processed.type) ? processed.type : 'image/jpeg'

    const base64Promise = (async () => {
      return new Promise((resolve, reject) => {
        const r = new FileReader()
        r.onload = () => resolve(String(r.result).split(',')[1] || '')
        r.onerror = () => reject(new Error('FileReader failed'))
        r.readAsDataURL(processed)
      })
    })()

    let base64
    try {
      base64 = await base64Promise
    } catch (err) {
      console.error('[RoomScan getDetails] base64 encode failed', err)
      setItemError(itemId, "Couldn't read details — try again")
      setItemInFlight(itemId, false)
      return
    }

    const hint = {}
    if (item.brand) hint.make = item.brand
    if (item.model_number) hint.model = item.model_number
    const typeHint = item.refined_label || item.label
    if (typeHint) hint.type = typeHint

    // Upload + identify-appliance run in parallel. One failing does not fail the other.
    const sessionId = response?.session_id || null
    const userId = (await supabase.auth.getUser())?.data?.user?.id

    const uploadTask = (async () => {
      if (!userId || !sessionId) {
        throw new Error('Session expired. Please retry the scan.')
      }
      const path = `${userId}/${sessionId}/details/${itemId}/${Date.now()}.jpg`
      const { error: upErr } = await supabase.storage
        .from('room-scans')
        .upload(path, processed, { contentType: 'image/jpeg', upsert: false })
      if (upErr) throw new Error(`upload: ${upErr.message}`)
      const { data: signed, error: signErr } = await supabase.storage
        .from('room-scans')
        .createSignedUrl(path, 60 * 60 * 24 * 365)
      if (signErr || !signed?.signedUrl) {
        throw new Error(`signed-url: ${signErr?.message || 'unknown'}`)
      }
      return signed.signedUrl
    })()

    const identifyTask = (async () => {
      const { data, error } = await supabase.functions.invoke('identify-appliance', {
        body: { imageBase64: base64, mediaType, hint },
      })
      if (error) throw new Error(`function: ${error.message || error}`)
      const text = data?.text
      if (typeof text !== 'string' || !text) {
        throw new Error('function: empty text response')
      }
      let parsed
      try {
        parsed = JSON.parse(text)
      } catch (parseErr) {
        // Try to salvage a JSON object embedded in surrounding text.
        const start = text.indexOf('{')
        const end = text.lastIndexOf('}')
        if (start >= 0 && end > start) {
          try { parsed = JSON.parse(text.slice(start, end + 1)) } catch { parsed = null }
        }
        if (!parsed) {
          console.error('[RoomScan getDetails] identify-appliance parse failed. Full text:', text)
          throw new Error('parse: invalid JSON')
        }
      }
      return parsed
    })()

    const [uploadResult, identifyResult] = await Promise.allSettled([uploadTask, identifyTask])

    let anyError = false
    let signedUrl = null
    let parsed = null

    if (uploadResult.status === 'fulfilled') {
      signedUrl = uploadResult.value
    } else {
      console.error('[RoomScan getDetails] upload failed:', uploadResult.reason?.message || uploadResult.reason)
      anyError = true
    }
    if (identifyResult.status === 'fulfilled') {
      parsed = identifyResult.value
    } else {
      console.error('[RoomScan getDetails] identify-appliance failed:', identifyResult.reason?.message || identifyResult.reason)
      anyError = true
    }

    // Apply fill-nulls merge + photo append to the item, in a single setItems call.
    setItems(prev => prev.map(it => {
      if (it.localId !== itemId) return it
      const next = { ...it }
      const isEmpty = v => v == null || (typeof v === 'string' && v.trim() === '')
      const allowedConf = c => c === 'high' || c === 'medium'

      if (parsed && typeof parsed === 'object') {
        if (isEmpty(next.brand) && typeof parsed.make === 'string' && parsed.make.trim() && allowedConf(parsed.make_confidence)) {
          next.brand = parsed.make
          next.brand_confidence = parsed.make_confidence
        }
        if (isEmpty(next.model_number) && typeof parsed.model === 'string' && parsed.model.trim() && allowedConf(parsed.model_confidence)) {
          next.model_number = parsed.model
          next.model_confidence = parsed.model_confidence
        }
        if (isEmpty(next.serial_number) && typeof parsed.serial === 'string' && parsed.serial.trim()) {
          next.serial_number = parsed.serial
        }
        if (isEmpty(next.manufactured) && typeof parsed.manufactured === 'string' && parsed.manufactured.trim()) {
          next.manufactured = parsed.manufactured
        }
      }

      if (signedUrl) {
        next.photo_urls = [...(next.photo_urls || []), signedUrl]
      }

      return next
    }))

    setItemInFlight(itemId, false)
    if (anyError) {
      setItemError(itemId, "Couldn't read details — try again")
    } else {
      flashItemSuccess(itemId)
    }
  }

  const handleAddManualItem = () => {
    const name = manualForm.name.trim()
    if (!name) return
    const newItem = {
      localId: nextLocalId('manual'),
      overlayNumber: null,
      label: name,
      refined_label: name,
      confidence_band: null,
      confidence_score: null,
      bounding_box: null,
      // Manual items aren't tied to any frame; times_seen=1 keeps the
      // "Seen in N frames" badge hidden.
      frame_index: null,
      frame_indices: [],
      times_seen: 1,
      thumbnail_url: null,
      claude_extracted: false,
      ai_extracted: false,
      should_log: true, // user explicitly added it
      selected: true,
      name,
      category: manualForm.category || 'Other',
      brand: manualForm.brand.trim(),
      brand_confidence: null,
      model_number: manualForm.model_number.trim(),
      model_confidence: null,
      serial_number: '',
      manufactured: '',
      color: manualForm.color.trim(),
      material: manualForm.material.trim(),
      size: manualForm.size.trim(),
      size_confidence: null,
      notes: '',
      photo_urls: [],
    }
    setItems(prev => [...prev, newItem])
    setManualForm({ name: '', category: 'Other', brand: '', model_number: '', color: '', material: '', size: '' })
    setShowManualAdd(false)
  }

  const handleSave = async () => {
    const itemsToSave = items.filter(it => it.selected)
    if (itemsToSave.length === 0) return
    setState('saving')
    try {
      const userResp = await supabase.auth.getUser()
      const userId = userResp?.data?.user?.id
      if (!userId) {
        setErrorMessage("Some items couldn't be saved. Please try again.")
        setErrorContext('save')
        setState('error')
        return
      }

      const sessionId = response?.session_id || null
      const results = await Promise.all(itemsToSave.map(item => {
        const payload = {
          room_id: room.id,
          home_id: homeId,
          user_id: userId,
          name: item.name?.trim() || item.refined_label || 'Item',
          category: item.category || null,
          brand: item.brand?.trim() || null,
          model_number: item.model_number?.trim() || null,
          color: item.color?.trim() || null,
          material: item.material?.trim() || null,
          ai_confidence: item.confidence_band || null,
          ai_extracted: item.ai_extracted !== false,
          position_hint: item.position_hint || null,
          bounding_box: item.bounding_box || null,
          source_capture_id: sessionId,
          detail_photo_url: item.thumbnail_url || null,
          serial_number: item.serial_number?.trim() || null,
          manufactured: item.manufactured?.trim() || null,
          notes: item.notes?.trim() || null,
          photo_urls: (item.photo_urls && item.photo_urls.length > 0) ? item.photo_urls : null,
        }
        return supabase.from('room_inventory').insert(payload).select().single()
      }))

      const errored = results.filter(r => r.error)
      if (errored.length > 0) {
        console.error('[RoomScan] save errors', errored.map(r => r.error))
        setErrorMessage("Some items couldn't be saved. Please try again.")
        setErrorContext('save')
        setState('error')
        return
      }

      const inserted = results.map(r => r.data).filter(Boolean)

      if (sessionId) {
        await supabase
          .from('room_scan_sessions')
          .update({ status: 'saved', items_saved: inserted.length })
          .eq('id', sessionId)
      }

      onSaved(inserted)
    } catch (err) {
      console.error('[RoomScan] save exception', err)
      setErrorMessage("Some items couldn't be saved. Please try again.")
      setErrorContext('save')
      setState('error')
    }
  }

  const handleErrorTryAgain = () => {
    if (errorContext === 'save') {
      handleSave()
    } else {
      setState('capture')
      setErrorMessage(null)
      setErrorContext(null)
      setResponse(null)
      setItems([])
      setEditingItemId(null)
      setEditDraft(null)
      setEditOriginal(null)
    }
  }

  // -- Layout primitives ------------------------------------------------------

  const overlay = {
    position: 'fixed', inset: 0, background: C.bg, zIndex: 1500,
    display: 'flex', flexDirection: 'column',
  }
  const header = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', borderBottom: `1px solid ${C.border}`,
    background: C.card, flexShrink: 0,
  }
  const headerTitle = {
    fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown,
  }
  const closeBtn = {
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: C.muted, padding: '6px', borderRadius: '8px', lineHeight: 0,
  }
  const scrollArea = {
    flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
  }
  const centerPane = {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '32px 24px', textAlign: 'center',
  }

  // -- Renderers --------------------------------------------------------------

  const renderHeader = (titleText, hideClose = false) => (
    <div style={header}>
      <div style={headerTitle}>{titleText}</div>
      {!hideClose && (
        <button
          onClick={handleHeaderClose}
          aria-label="Close"
          style={closeBtn}
        >
          <X size={22} color={C.muted} />
        </button>
      )}
    </div>
  )

  const renderCapture = () => (
    <>
      {renderHeader(`${room?.name || 'Room'} Photo`)}
      <div style={centerPane}>
        <Camera size={96} strokeWidth={1.2} color={C.amber} />
        <div style={{ fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown, marginTop: '20px' }}>
          Scan this room
        </div>
        <div style={{ fontFamily: fs, fontSize: '14px', color: C.muted, marginTop: '10px', maxWidth: '320px', lineHeight: 1.55 }}>
          Take one wide photo. We'll find your furniture, electronics, and belongings and add them to inventory.
        </div>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />
        <input
          ref={libraryInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />
        <button
          onClick={() => cameraInputRef.current?.click()}
          style={{
            marginTop: '28px',
            background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
            color: '#fff', border: 'none', borderRadius: '10px',
            padding: '14px 28px', fontFamily: f, fontSize: '15px',
            fontWeight: '700', cursor: 'pointer', letterSpacing: '0.3px',
            boxShadow: '0 2px 6px rgba(184,114,31,0.25)',
            display: 'inline-flex', alignItems: 'center', gap: '10px',
          }}
        >
          <Camera size={18} strokeWidth={2} color="#fff" />
          Take Photo
        </button>
        <button
          onClick={onClose}
          style={{
            marginTop: '14px', background: 'transparent', border: 'none',
            color: C.muted, fontFamily: fs, fontSize: '14px',
            textDecoration: 'underline', cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </>
  )

  const renderProcessing = () => (
    <>
      {renderHeader(`${room?.name || 'Room'} Photo`, true)}
      <div style={centerPane}>
        <Loader
          size={64}
          color={C.amber}
          strokeWidth={1.5}
          style={{ animation: 'roomscan-spin 1s linear infinite' }}
        />
        <div style={{ fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown, marginTop: '20px' }}>
          Looking around your room…
        </div>
        <div style={{ fontFamily: fs, fontSize: '14px', color: C.muted, marginTop: '10px', maxWidth: '320px', lineHeight: 1.55 }}>
          Identifying what's here. This usually takes about 15 seconds.
        </div>
      </div>
    </>
  )

  const renderSaving = () => (
    <>
      {renderHeader(`${room?.name || 'Room'} Photo`, true)}
      <div style={centerPane}>
        <Loader
          size={64}
          color={C.amber}
          strokeWidth={1.5}
          style={{ animation: 'roomscan-spin 1s linear infinite' }}
        />
        <div style={{ fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown, marginTop: '20px' }}>
          Saving {items.filter(it => it.selected).length} item{items.filter(it => it.selected).length === 1 ? '' : 's'}…
        </div>
      </div>
    </>
  )

  const renderError = () => (
    <>
      {renderHeader(`${room?.name || 'Room'} Photo`)}
      <div style={centerPane}>
        <div style={{ fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown, marginBottom: '12px' }}>
          {errorContext === 'save' ? 'Save didn\'t finish' : "Let's try that again"}
        </div>
        <div style={{ fontFamily: fs, fontSize: '14px', color: C.muted, maxWidth: '340px', lineHeight: 1.6, marginBottom: '24px' }}>
          {errorMessage}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleErrorTryAgain}
            style={{
              background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
              color: '#fff', border: 'none', borderRadius: '10px',
              padding: '12px 22px', fontFamily: f, fontSize: '14px',
              fontWeight: '700', cursor: 'pointer',
            }}
          >
            Try Again
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', color: C.brown,
              border: `1px solid ${C.border}`, borderRadius: '10px',
              padding: '12px 22px', fontFamily: fs, fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </>
  )

  const renderReview = () => {
    // v6 server returns photo_urls (plural) for video/multi_photo and
    // photo_urls: [photo_url] for photo mode. Fall back to legacy photo_url.
    const photoUrls = Array.isArray(response?.photo_urls) && response.photo_urls.length > 0
      ? response.photo_urls
      : (response?.photo_url ? [response.photo_url] : [])
    // Hero frame (D1): the frame index with the most key items. Single-photo
    // sessions collapse to frame 0 trivially.
    const heroFrameIndex = (() => {
      if (photoUrls.length <= 1) return 0
      const counts = new Map()
      for (const it of items) {
        if (it.frame_index == null || it.should_log === false) continue
        counts.set(it.frame_index, (counts.get(it.frame_index) || 0) + 1)
      }
      if (counts.size === 0) return 0
      let bestIdx = 0
      let bestCount = -1
      for (const [idx, n] of counts) {
        if (n > bestCount || (n === bestCount && idx < bestIdx)) {
          bestIdx = idx
          bestCount = n
        }
      }
      return bestIdx
    })()
    const photoUrl = photoUrls[heroFrameIndex] || photoUrls[0] || null
    const isMultiFrame = photoUrls.length > 1
    return (
      <>
        {renderHeader(`${room?.name || 'Room'} Photo`)}
        {/* Hidden input for per-item "Get details" close-up capture */}
        <input
          ref={detailsInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleDetailsFileSelected}
        />
        <div style={scrollArea}>
          {/* Photo with bounding-box overlays */}
          <div ref={photoSectionRef} style={{ display: 'flex', justifyContent: 'center', background: '#000' }}>
            <div style={{ position: 'relative', display: 'inline-block', maxHeight: '35vh' }}>
              {photoUrl && (
                <img
                  src={photoUrl}
                  alt="Room scan"
                  style={{ display: 'block', maxWidth: '100%', maxHeight: '35vh', width: 'auto', height: 'auto' }}
                />
              )}
              {items.filter(it => it.bounding_box && (!isMultiFrame || it.frame_index === heroFrameIndex)).map(item => {
                const b = item.bounding_box
                return (
                  <button
                    key={item.localId}
                    onClick={() => openEditView(item)}
                    aria-label={`Item ${item.overlayNumber}: ${item.refined_label}`}
                    style={{
                      position: 'absolute',
                      left: `${b.x_min * 100}%`,
                      top: `${b.y_min * 100}%`,
                      width: `${(b.x_max - b.x_min) * 100}%`,
                      height: `${(b.y_max - b.y_min) * 100}%`,
                      border: `2px solid ${C.amber}`,
                      background: 'rgba(184,114,31,0.1)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {item.overlayNumber != null && (
                      <span style={{
                        position: 'absolute', top: '-10px', left: '-10px',
                        background: C.amber, color: '#fff',
                        width: '22px', height: '22px', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: f, fontSize: '12px', fontWeight: '700',
                        border: '2px solid #fff',
                      }}>
                        {item.overlayNumber}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Detection summary / curation header */}
          {(() => {
            const totalDetected = items.length
            const keyCount = items.filter(it => it.should_log !== false).length
            const skippedCount = totalDetected - keyCount
            if (skippedCount === 0) {
              return (
                <div style={{
                  fontFamily: fs, fontSize: '13px', color: C.muted,
                  padding: '14px 18px 6px',
                }}>
                  {totalDetected} item{totalDetected === 1 ? '' : 's'} detected — review and edit below
                </div>
              )
            }
            return (
              <div style={{ padding: '14px 18px 6px' }}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start',
                  justifyContent: 'space-between', gap: '12px',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: f, fontSize: '16px', fontWeight: '700', color: C.brown }}>
                      {keyCount} key item{keyCount === 1 ? '' : 's'} identified
                    </div>
                    <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted, marginTop: '4px', lineHeight: 1.5 }}>
                      Casavio focused on the items most worth documenting from your scan.
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAll(v => !v)}
                    style={{
                      flexShrink: 0,
                      background: 'transparent', color: C.amber,
                      border: `1px solid ${C.border}`, borderRadius: '8px',
                      padding: '8px 12px', fontFamily: fs, fontSize: '12px',
                      fontWeight: '600', cursor: 'pointer',
                    }}
                  >
                    {showAll ? 'Hide non-essential' : `See all ${totalDetected} items`}
                  </button>
                </div>
              </div>
            )
          })()}

          {/* Cards */}
          <div style={{ padding: '4px 14px 120px' }}>
            {items.filter(it => showAll || it.should_log !== false).map(item => {
              const isSkipped = item.should_log === false
              return (
                <div
                  key={item.localId}
                  ref={el => { if (el) cardRefs.current[item.localId] = el }}
                  style={{
                    background: isSkipped ? C.bg : C.card,
                    border: `1px solid ${C.border}`,
                    borderRadius: '12px',
                    marginBottom: '10px',
                    overflow: 'hidden',
                  }}
                >
                  {/* Card — tap anywhere to open the full-screen edit view.
                      Title gets the full row width; action controls (Save /
                      Get details / Remove) sit on a second row below so long
                      names like "Dark Wood Writing Desk" don't truncate. */}
                  <div
                    onClick={() => openEditView(item)}
                    style={{
                      padding: '10px 12px', cursor: 'pointer',
                    }}
                  >
                    {/* Row 1: number + thumb + title block + chevron */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {item.overlayNumber != null ? (
                        <span style={{
                          flexShrink: 0,
                          background: C.amber, color: '#fff',
                          width: '24px', height: '24px', borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: f, fontSize: '12px', fontWeight: '700',
                        }}>
                          {item.overlayNumber}
                        </span>
                      ) : (
                        <span style={{
                          flexShrink: 0,
                          background: C.bg, color: C.muted,
                          width: '24px', height: '24px', borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontFamily: f, fontSize: '12px', fontWeight: '700',
                          border: `1px dashed ${C.border}`,
                        }}>
                          +
                        </span>
                      )}
                      {item.thumbnail_url ? (
                        <img
                          src={item.thumbnail_url}
                          alt=""
                          style={{
                            width: isSkipped ? '50px' : '60px',
                            height: isSkipped ? '50px' : '60px',
                            objectFit: 'cover', borderRadius: '8px',
                            flexShrink: 0, background: C.bg,
                          }}
                        />
                      ) : (
                        <div style={{
                          width: isSkipped ? '50px' : '60px',
                          height: isSkipped ? '50px' : '60px',
                          borderRadius: '8px', background: C.bg,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                          color: C.muted, fontFamily: fs, fontSize: '10px',
                        }}>
                          no photo
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: f, fontSize: '16px', color: C.brown,
                          fontWeight: '700', lineHeight: 1.25,
                          wordBreak: 'break-word',
                        }}>
                          {item.name || item.refined_label}
                        </div>
                        <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span style={{
                            display: 'inline-block',
                            background: C.bg, color: C.brown,
                            fontFamily: fs, fontSize: '11px',
                            padding: '2px 8px', borderRadius: '999px',
                            border: `1px solid ${C.border}`,
                          }}>
                            {item.category}
                          </span>
                          {item.times_seen > 1 && (
                            <span
                              title={`Detected in frames ${(item.frame_indices || []).join(', ')}`}
                              style={{
                                display: 'inline-block',
                                background: '#FBF3E8', color: C.amber,
                                fontFamily: fs, fontSize: '11px', fontWeight: '600',
                                padding: '2px 8px', borderRadius: '999px',
                                border: `1px solid ${C.amber}33`,
                              }}
                            >
                              Seen in {item.times_seen} frames
                            </span>
                          )}
                          {detailsSuccess[item.localId] && (
                            <span style={{
                              display: 'inline-block',
                              background: C.success, color: '#fff',
                              fontFamily: fs, fontSize: '11px', fontWeight: '600',
                              padding: '2px 8px', borderRadius: '999px',
                            }}>
                              ✓ Updated
                            </span>
                          )}
                          {detailsErrors[item.localId] && (
                            <span style={{
                              fontFamily: fs, fontSize: '11px',
                              color: C.danger,
                            }}>
                              {detailsErrors[item.localId]}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={20} color={C.muted} style={{ flexShrink: 0 }} />
                    </div>

                    {/* Row 2: action cluster — full card width, doesn't squeeze the name above */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      marginTop: '10px',
                    }}>
                      <label
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '5px',
                          fontFamily: fs, fontSize: '12px', color: C.muted,
                          cursor: 'pointer', flexShrink: 0,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!item.selected}
                          onChange={(e) => updateItem(item.localId, { selected: e.target.checked })}
                          style={{ accentColor: C.amber, width: '16px', height: '16px', margin: 0 }}
                          aria-label="Save this item"
                        />
                        Save
                      </label>
                      {detailsInFlight[item.localId] ? (
                        <div
                          aria-label="Reading details"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '6px 10px', borderRadius: '8px',
                            background: C.bg, border: `1px solid ${C.border}`,
                            flexShrink: 0,
                          }}
                        >
                          <div
                            style={{
                              width: '12px', height: '12px',
                              border: `2px solid ${C.amber}33`,
                              borderTopColor: C.amber,
                              borderRadius: '50%',
                              animation: 'roomscan-spin 0.9s linear infinite',
                            }}
                          />
                          <span style={{ fontFamily: fs, fontSize: '11px', color: C.muted }}>Reading…</span>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleGetDetailsClick(item) }}
                          aria-label="Get details"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '5px',
                            background: 'transparent', color: C.brown,
                            border: `1px solid ${C.border}`, borderRadius: '8px',
                            padding: '5px 9px', fontFamily: fs, fontSize: '11px',
                            fontWeight: '600', cursor: 'pointer',
                            flexShrink: 0, lineHeight: 1.1,
                          }}
                        >
                          <Camera size={13} strokeWidth={2} color={C.brown} />
                          Get details
                        </button>
                      )}
                      <div style={{ flex: 1 }} />
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteItem(item.localId) }}
                        aria-label="Remove item"
                        style={{
                          background: 'transparent', border: 'none',
                          cursor: 'pointer', padding: '6px',
                          borderRadius: '8px', color: C.muted, lineHeight: 0,
                          flexShrink: 0,
                        }}
                      >
                        <Trash2 size={18} color={C.muted} />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Bottom action bar */}
        <div style={{
          flexShrink: 0,
          padding: '12px 14px 18px',
          borderTop: `1px solid ${C.border}`,
          background: C.card,
          display: 'flex', flexDirection: 'column', gap: '10px',
        }}>
          <button
            onClick={() => setShowManualAdd(true)}
            style={{
              background: 'transparent', color: C.brown,
              border: `1px solid ${C.border}`, borderRadius: '10px',
              padding: '11px', fontFamily: fs, fontSize: '14px',
              fontWeight: '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            }}
          >
            <Plus size={16} strokeWidth={2} color={C.brown} />
            Add an item we missed
          </button>
          {(() => {
            const selectedCount = items.filter(it => it.selected).length
            return (
              <button
                onClick={handleSave}
                disabled={selectedCount === 0}
                style={{
                  background: selectedCount === 0
                    ? C.border
                    : `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                  color: '#fff', border: 'none', borderRadius: '10px',
                  padding: '14px', fontFamily: f, fontSize: '15px',
                  fontWeight: '700',
                  cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
                  opacity: selectedCount === 0 ? 0.6 : 1,
                  boxShadow: selectedCount === 0 ? 'none' : '0 2px 6px rgba(184,114,31,0.25)',
                }}
              >
                Save {selectedCount} item{selectedCount === 1 ? '' : 's'} to inventory
              </button>
            )
          })()}
        </div>

        {/* Close confirmation */}
        {confirmDiscard && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
            zIndex: 1600, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}>
            <div style={{
              background: C.card, borderRadius: '14px', padding: '22px',
              maxWidth: '360px', width: '100%',
            }}>
              <div style={{ fontFamily: f, fontSize: '17px', fontWeight: '700', color: C.brown, marginBottom: '8px' }}>
                Discard scanned items?
              </div>
              <div style={{ fontFamily: fs, fontSize: '13px', color: C.brown, lineHeight: 1.55, marginBottom: '18px' }}>
                You'll lose {items.length} detected item{items.length === 1 ? '' : 's'} that {items.length === 1 ? "hasn't" : "haven't"} been saved yet.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  onClick={() => setConfirmDiscard(false)}
                  style={{
                    background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                    color: '#fff', border: 'none', borderRadius: '8px',
                    padding: '12px', fontFamily: f, fontSize: '14px',
                    fontWeight: '700', cursor: 'pointer',
                  }}
                >
                  Keep reviewing
                </button>
                <button
                  onClick={() => { setConfirmDiscard(false); onClose() }}
                  style={{
                    background: 'transparent', color: C.muted,
                    border: `1px solid ${C.border}`, borderRadius: '8px',
                    padding: '12px', fontFamily: fs, fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Manual add modal */}
        {showManualAdd && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
            zIndex: 1600, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}>
            <div style={{
              background: C.card, borderRadius: '20px 20px 0 0',
              width: '100%', maxWidth: '560px',
              maxHeight: '90vh', overflowY: 'auto',
              padding: '20px 18px 30px',
            }}>
              <div style={{ width: '40px', height: '4px', background: C.border, borderRadius: '2px', margin: '0 auto 18px' }} />
              <div style={{ fontFamily: f, fontSize: '18px', fontWeight: '700', color: C.brown, marginBottom: '4px' }}>
                Add an item
              </div>
              <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted, marginBottom: '16px' }}>
                Anything the scan missed.
              </div>
              <label style={lbl}>Name</label>
              <input
                value={manualForm.name}
                onChange={e => setManualForm(p => ({ ...p, name: e.target.value }))}
                style={inp}
                autoFocus
              />
              <div style={{ marginTop: '12px' }}>
                <label style={lbl}>Category</label>
                <select
                  value={manualForm.category}
                  onChange={e => setManualForm(p => ({ ...p, category: e.target.value }))}
                  style={{ ...inp, background: '#fff' }}
                >
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ marginTop: '12px' }}>
                <label style={lbl}>Brand</label>
                <input
                  value={manualForm.brand}
                  onChange={e => setManualForm(p => ({ ...p, brand: e.target.value }))}
                  style={inp}
                />
              </div>
              <div style={{ marginTop: '12px' }}>
                <label style={lbl}>Model number</label>
                <input
                  value={manualForm.model_number}
                  onChange={e => setManualForm(p => ({ ...p, model_number: e.target.value }))}
                  style={inp}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Color</label>
                  <input
                    value={manualForm.color}
                    onChange={e => setManualForm(p => ({ ...p, color: e.target.value }))}
                    style={inp}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lbl}>Material</label>
                  <input
                    value={manualForm.material}
                    onChange={e => setManualForm(p => ({ ...p, material: e.target.value }))}
                    style={inp}
                  />
                </div>
              </div>
              <div style={{ marginTop: '12px' }}>
                <label style={lbl}>Size</label>
                <input
                  value={manualForm.size}
                  onChange={e => setManualForm(p => ({ ...p, size: e.target.value }))}
                  style={inp}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  onClick={() => { setShowManualAdd(false); setManualForm({ name: '', category: 'Other', brand: '', model_number: '', color: '', material: '', size: '' }) }}
                  style={{
                    flex: 1, background: 'transparent', color: C.muted,
                    border: `1px solid ${C.border}`, borderRadius: '10px',
                    padding: '12px', fontFamily: fs, fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddManualItem}
                  disabled={!manualForm.name.trim()}
                  style={{
                    flex: 1,
                    background: !manualForm.name.trim()
                      ? C.border
                      : `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                    color: '#fff', border: 'none', borderRadius: '10px',
                    padding: '12px', fontFamily: f, fontSize: '14px',
                    fontWeight: '700',
                    cursor: !manualForm.name.trim() ? 'not-allowed' : 'pointer',
                    opacity: !manualForm.name.trim() ? 0.6 : 1,
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  const renderEditView = () => {
    if (!editDraft) return null
    // In multi-frame mode show the item on the frame it was actually detected
    // on; fall back to single-photo / first frame for legacy responses.
    const editPhotoUrls = Array.isArray(response?.photo_urls) && response.photo_urls.length > 0
      ? response.photo_urls
      : (response?.photo_url ? [response.photo_url] : [])
    const photoUrl = (editDraft.frame_index != null && editPhotoUrls[editDraft.frame_index])
      || editPhotoUrls[0]
      || null
    const b = editDraft.bounding_box
    const detailPhotos = editDraft.photo_urls || []
    const atPhotoCap = detailPhotos.length >= 10

    return (
      <div style={{
        position: 'fixed', inset: 0, background: C.bg, zIndex: 1700,
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={header}>
          <div style={headerTitle}>Edit Item</div>
          <button onClick={handleEditClose} aria-label="Close" style={closeBtn}>
            <X size={22} color={C.muted} />
          </button>
        </div>

        <div style={scrollArea}>
          {/* Bounding box reference + AI thumbnail */}
          <div style={{ padding: '14px 14px 0' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              {photoUrl && b && (
                <div style={{
                  position: 'relative', flexShrink: 0,
                  background: '#000', borderRadius: '8px', overflow: 'hidden',
                  width: '140px', height: '105px',
                }}>
                  <img
                    src={photoUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.55 }}
                  />
                  <div style={{
                    position: 'absolute',
                    left: `${b.x_min * 100}%`,
                    top: `${b.y_min * 100}%`,
                    width: `${(b.x_max - b.x_min) * 100}%`,
                    height: `${(b.y_max - b.y_min) * 100}%`,
                    border: `2px solid ${C.amber}`,
                    background: 'rgba(184,114,31,0.18)',
                    borderRadius: '3px',
                  }} />
                  {editDraft.overlayNumber != null && (
                    <span style={{
                      position: 'absolute', top: '6px', left: '6px',
                      background: C.amber, color: '#fff',
                      width: '22px', height: '22px', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: f, fontSize: '12px', fontWeight: '700',
                      border: '2px solid #fff',
                    }}>
                      {editDraft.overlayNumber}
                    </span>
                  )}
                </div>
              )}
              {editDraft.thumbnail_url && (
                <img
                  src={editDraft.thumbnail_url}
                  alt=""
                  style={{
                    width: '120px', height: '120px',
                    objectFit: 'cover', borderRadius: '10px',
                    background: C.bg, flexShrink: 0,
                  }}
                />
              )}
            </div>
          </div>

          {/* Detail Photos */}
          <div style={{ padding: '20px 14px 0' }}>
            <div style={{
              fontSize: '10px', color: C.muted, letterSpacing: '2px',
              textTransform: 'uppercase', fontFamily: fs, fontWeight: '700',
              marginBottom: '10px',
            }}>
              Detail Photos
            </div>
            {detailPhotos.length > 0 && (
              <div style={{
                display: 'flex', gap: '8px', overflowX: 'auto',
                paddingBottom: '14px', WebkitOverflowScrolling: 'touch',
              }}>
                {detailPhotos.map((url, idx) => {
                  const extraction = editDetailExtractions[url]
                  return (
                    <div key={url + idx} style={{ position: 'relative', flexShrink: 0 }}>
                      <img
                        src={url}
                        alt=""
                        style={{
                          width: '80px', height: '80px',
                          objectFit: 'cover', borderRadius: '8px',
                          background: C.bg,
                          display: 'block',
                        }}
                      />
                      {/* In-flight spinner overlay while extraction runs */}
                      {extraction?.state === 'extracting' && (
                        <div style={{
                          position: 'absolute', inset: 0,
                          background: 'rgba(0,0,0,0.45)', borderRadius: '8px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          gap: '6px', flexDirection: 'column',
                        }}>
                          <div style={{
                            width: '18px', height: '18px',
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderTopColor: '#fff', borderRadius: '50%',
                            animation: 'roomscan-spin 0.9s linear infinite',
                          }} />
                          <span style={{ fontFamily: fs, fontSize: '9px', color: '#fff', fontWeight: '600' }}>Reading…</span>
                        </div>
                      )}
                      {/* Success pill */}
                      {extraction?.state === 'success' && (
                        <div style={{
                          position: 'absolute', bottom: '-10px', left: '-4px', right: '-4px',
                          background: C.success, color: '#fff',
                          fontFamily: fs, fontSize: '10px', fontWeight: '600',
                          padding: '3px 6px', borderRadius: '6px', textAlign: 'center',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                        }}>
                          ✓ {extraction.summary}
                        </div>
                      )}
                      {/* Error indicator */}
                      {extraction?.state === 'error' && (
                        <div style={{
                          position: 'absolute', bottom: '-10px', left: '-4px', right: '-4px',
                          background: C.danger, color: '#fff',
                          fontFamily: fs, fontSize: '10px', fontWeight: '600',
                          padding: '3px 6px', borderRadius: '6px', textAlign: 'center',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                        }}>
                          Couldn't read
                        </div>
                      )}
                      <button
                        onClick={() => removeEditPhoto(idx)}
                        aria-label="Remove photo"
                        style={{
                          position: 'absolute', top: '-6px', right: '-6px',
                          width: '22px', height: '22px', borderRadius: '50%',
                          background: '#fff', border: `1px solid ${C.border}`,
                          cursor: 'pointer', padding: 0, lineHeight: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <X size={12} color={C.muted} />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}

            <input
              ref={editCameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleEditPhotoSelect}
            />
            <input
              ref={editLibraryRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleEditPhotoSelect}
            />

            {/* Toggle: when on, each new Detail Photo runs a close-up Claude
                extraction and fill-nulls-merges into the item. */}
            <label
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                fontFamily: fs, fontSize: '12px', color: C.muted,
                cursor: 'pointer', marginTop: detailPhotos.length > 0 ? '10px' : 0,
                marginBottom: '10px',
              }}
            >
              <input
                type="checkbox"
                checked={editExtractEnabled}
                onChange={(e) => setEditExtractEnabled(e.target.checked)}
                style={{ accentColor: C.amber, width: '14px', height: '14px', margin: 0 }}
              />
              Read details from new photos
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={() => editCameraRef.current?.click()}
                disabled={atPhotoCap || editPhotoUploading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  background: atPhotoCap || editPhotoUploading ? C.bg : `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                  color: atPhotoCap || editPhotoUploading ? C.muted : '#fff',
                  border: 'none', borderRadius: '10px',
                  padding: '12px 14px', fontFamily: f, fontSize: '14px',
                  fontWeight: '700',
                  cursor: atPhotoCap || editPhotoUploading ? 'not-allowed' : 'pointer',
                  opacity: atPhotoCap || editPhotoUploading ? 0.7 : 1,
                  justifyContent: 'flex-start',
                }}
              >
                <Camera size={16} strokeWidth={2} color={atPhotoCap || editPhotoUploading ? C.muted : '#fff'} />
                {editPhotoUploading ? 'Uploading…' : 'Take a Photo'}
              </button>
              <button
                onClick={() => editLibraryRef.current?.click()}
                disabled={atPhotoCap || editPhotoUploading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  background: '#fff', color: C.brown,
                  border: `1px solid ${C.border}`, borderRadius: '10px',
                  padding: '12px 14px', fontFamily: fs, fontSize: '14px',
                  fontWeight: '600',
                  cursor: atPhotoCap || editPhotoUploading ? 'not-allowed' : 'pointer',
                  opacity: atPhotoCap || editPhotoUploading ? 0.5 : 1,
                  justifyContent: 'flex-start',
                }}
              >
                <ImageIcon size={16} strokeWidth={2} color={C.brown} />
                Add from Camera Roll
              </button>
              {atPhotoCap && (
                <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted }}>
                  Limit reached — 10 detail photos per item.
                </div>
              )}
              {editPhotoError && (
                <div style={{ fontFamily: fs, fontSize: '12px', color: C.danger }}>
                  {editPhotoError}
                </div>
              )}
            </div>
          </div>

          {/* Item Details */}
          <div style={{ padding: '24px 14px 0' }}>
            <div style={{
              fontSize: '10px', color: C.muted, letterSpacing: '2px',
              textTransform: 'uppercase', fontFamily: fs, fontWeight: '700',
              marginBottom: '10px',
            }}>
              Item Details
            </div>

            <div>
              <label style={lbl}>Name</label>
              <input
                value={editDraft.name || ''}
                onChange={e => setEditField('name', e.target.value)}
                style={inp}
              />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}>
                <ConfDot conf={editDraft.brand_confidence} /> Brand
              </label>
              <input
                value={editDraft.brand || ''}
                onChange={e => setEditField('brand', e.target.value)}
                style={inp}
                placeholder="—"
              />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}>
                <ConfDot conf={editDraft.model_confidence} /> Model
              </label>
              <input
                value={editDraft.model_number || ''}
                onChange={e => setEditField('model_number', e.target.value)}
                style={inp}
                placeholder="—"
              />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}>Serial</label>
              <input
                value={editDraft.serial_number || ''}
                onChange={e => setEditField('serial_number', e.target.value)}
                style={inp}
                placeholder="—"
              />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}>Category</label>
              <select
                value={editDraft.category || 'Other'}
                onChange={e => setEditField('category', e.target.value)}
                style={{ ...inp, background: '#fff' }}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Color</label>
                <input
                  value={editDraft.color || ''}
                  onChange={e => setEditField('color', e.target.value)}
                  style={inp}
                  placeholder="—"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Material</label>
                <input
                  value={editDraft.material || ''}
                  onChange={e => setEditField('material', e.target.value)}
                  style={inp}
                  placeholder="—"
                />
              </div>
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}>Notes</label>
              <textarea
                value={editDraft.notes || ''}
                onChange={e => setEditField('notes', e.target.value)}
                rows={3}
                style={{ ...inp, fontFamily: fs, resize: 'vertical', minHeight: '78px' }}
                placeholder="—"
              />
            </div>
          </div>

          {/* Remove this item */}
          <div style={{ padding: '24px 14px 12px' }}>
            <button
              onClick={handleEditDelete}
              style={{
                width: '100%', background: 'transparent',
                color: C.danger,
                border: `1px solid ${C.border}`, borderRadius: '10px',
                padding: '12px', fontFamily: f, fontSize: '14px',
                fontWeight: '700', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}
            >
              <Trash2 size={16} color={C.danger} strokeWidth={2} />
              Remove this item
            </button>
          </div>

          <div style={{ height: '24px' }} />
        </div>

        {/* Sticky bottom save bar */}
        <div style={{
          flexShrink: 0,
          padding: '12px 14px 18px',
          borderTop: `1px solid ${C.border}`,
          background: C.card,
        }}>
          <button
            onClick={() => closeEditView(true)}
            style={{
              width: '100%',
              background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
              color: '#fff', border: 'none', borderRadius: '10px',
              padding: '14px', fontFamily: f, fontSize: '15px',
              fontWeight: '700', cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(184,114,31,0.25)',
            }}
          >
            Save changes
          </button>
        </div>

        {/* Discard confirm */}
        {editConfirmDiscard && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
            zIndex: 1800, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}>
            <div style={{
              background: C.card, borderRadius: '14px', padding: '22px',
              maxWidth: '360px', width: '100%',
            }}>
              <div style={{ fontFamily: f, fontSize: '17px', fontWeight: '700', color: C.brown, marginBottom: '8px' }}>
                Discard changes to this item?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '18px' }}>
                <button
                  onClick={() => setEditConfirmDiscard(false)}
                  style={{
                    background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                    color: '#fff', border: 'none', borderRadius: '8px',
                    padding: '12px', fontFamily: f, fontSize: '14px',
                    fontWeight: '700', cursor: 'pointer',
                  }}
                >
                  Keep editing
                </button>
                <button
                  onClick={() => { setEditConfirmDiscard(false); closeEditView(false) }}
                  style={{
                    background: 'transparent', color: C.muted,
                    border: `1px solid ${C.border}`, borderRadius: '8px',
                    padding: '12px', fontFamily: fs, fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirm */}
        {editConfirmDelete && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
            zIndex: 1800, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}>
            <div style={{
              background: C.card, borderRadius: '14px', padding: '22px',
              maxWidth: '360px', width: '100%',
            }}>
              <div style={{ fontFamily: f, fontSize: '17px', fontWeight: '700', color: C.brown, marginBottom: '8px' }}>
                Remove this item from the scan?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '18px' }}>
                <button
                  onClick={() => setEditConfirmDelete(false)}
                  style={{
                    background: 'transparent', color: C.brown,
                    border: `1px solid ${C.border}`, borderRadius: '8px',
                    padding: '12px', fontFamily: fs, fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmEditDelete}
                  style={{
                    background: C.danger, color: '#fff',
                    border: 'none', borderRadius: '8px',
                    padding: '12px', fontFamily: f, fontSize: '14px',
                    fontWeight: '700', cursor: 'pointer',
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={overlay}>
      <style>{SPIN_STYLE}</style>
      {state === 'capture' && renderCapture()}
      {state === 'processing' && renderProcessing()}
      {state === 'review' && renderReview()}
      {state === 'saving' && renderSaving()}
      {state === 'error' && renderError()}
      {editingItemId && renderEditView()}
    </div>
  )
}
